import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Brain,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Activity,
  ShieldAlert,
  Sparkles,
  Target,
  Zap,
  ChevronRight,
} from "lucide-react";
import { apiGet } from "@/lib/api";

const THINKING_STEPS = [
  "Scanning market data...",
  "Checking market structure...",
  "Reading liquidity...",
  "Comparing winning patterns...",
  "Studying losing patterns...",
  "Calculating confidence...",
];

const SIM_KEY = "nakedchart_learning_simulation";

function normalizeCurrentSignal(data) {
  const current = data?.current_signal ?? data?.current_signal_analysis ?? data?.current_signals;

  if (!current) return null;
  if (Array.isArray(current)) return current[0] || null;
  return current;
}

function getRobotMode(learning, simulation) {
  if (simulation) return simulation;

  if (!learning) return "idle";

  const current = normalizeCurrentSignal(learning);

  const recommendation = String(
    current?.recommendation ||
      learning?.recommendation ||
      ""
  ).toUpperCase();

  const risk = String(
    current?.risk ||
      current?.status ||
      ""
  ).toUpperCase();

  if (recommendation.includes("AVOID") || risk === "HIGH") return "danger";
  if (recommendation.includes("CAUTION") || risk === "ELEVATED") return "caution";
  if (recommendation.includes("FAVORABLE")) return "good";

  if (
    recommendation.includes("WAIT") ||
    risk === "UNKNOWN" ||
    risk === "INSUFFICIENT_DATA"
  ) {
    return "thinking";
  }

  return "idle";
}

const MODE_INFO = {
  idle: {
    label: "NO SIGNAL",
    color: "text-slate-300",
    description: "Robot sedang menunggu signal berikutnya.",
  },
  thinking: {
    label: "LEARNING",
    color: "text-cyan-300",
    description: "Robot sedang membaca dan membandingkan pola.",
  },
  good: {
    label: "MARKET BAGUS",
    color: "text-emerald-300",
    description: "Pattern terlihat lebih mendukung berdasarkan data historis.",
  },
  caution: {
    label: "CAUTION",
    color: "text-yellow-300",
    description: "Robot menemukan kondisi yang perlu diperhatikan.",
  },
  danger: {
    label: "HIGH RISK",
    color: "text-red-300",
    description: "Robot mendeteksi kondisi berisiko tinggi.",
  },
  win: {
    label: "WIN",
    color: "text-emerald-300",
    description: "Trade berhasil. Robot menyimpan pola kemenangan.",
  },
  loss: {
    label: "LOSS",
    color: "text-orange-300",
    description: "Trade gagal. Robot menganalisis penyebabnya.",
  },
};

function RobotLearningV2({ mode = "idle" }) {
  const info = MODE_INFO[mode] || MODE_INFO.idle;

  return (
    <div className={`robot-scene robot-mode-${mode}`}>
      <div className="robot-stars">
        <span>✦</span>
        <span>·</span>
        <span>✦</span>
        <span>·</span>
        <span>✧</span>
      </div>

      {mode === "good" && (
        <div className="robot-bird" aria-hidden="true">
          🐦
        </div>
      )}

      {mode === "caution" && (
        <div className="robot-mouse" aria-hidden="true">
          🐭
        </div>
      )}

      {mode === "danger" && (
        <>
          <div className="robot-bee bee-one">🐝</div>
          <div className="robot-bee bee-two">🐝</div>
          <div className="robot-bee bee-three">🐝</div>
          <div className="robot-guardian">
            <div className="guardian-head" />
            <div className="guardian-body" />
            <div className="guardian-shield">🛡️</div>
          </div>
        </>
      )}

      {(mode === "win" || mode === "loss") && (
        <div className="robot-particles" aria-hidden="true">
          <span>✦</span>
          <span>✧</span>
          <span>•</span>
          <span>✦</span>
          <span>✧</span>
        </div>
      )}

      <div className="learning-robot-v2">
        <div className="robot-aura-v2" />

        <div className="robot-antenna-v2">
          <span />
        </div>

        <div className="robot-head-v2">
          <div className="robot-ear robot-ear-left" />
          <div className="robot-ear robot-ear-right" />

          <div className="robot-eyes-v2">
            <span className="robot-eye-v2" />
            <span className="robot-eye-v2" />
          </div>

          <div className="robot-mouth-v2">
            {mode === "good" || mode === "win"
              ? "◡"
              : mode === "danger"
                ? "︿"
                : mode === "loss"
                  ? "︵"
                  : mode === "caution"
                    ? "—"
                    : mode === "thinking"
                      ? "…"
                      : "•"}
          </div>

          {(mode === "caution" || mode === "danger") && (
            <div className="robot-question">
              {mode === "danger" ? "!" : "?"}
            </div>
          )}
        </div>

        <div className="robot-neck-v2" />

        <div className="robot-body-v2">
          <div className="robot-chest-v2">
            <Brain size={20} />
          </div>

          <div className="robot-panel-v2">
            <span />
            <span />
            <span />
          </div>

          <div className="robot-arm-v2 robot-arm-left-v2" />
          <div className="robot-arm-v2 robot-arm-right-v2" />

          {mode === "win" && <div className="robot-hand-wave">✋</div>}
        </div>

        <div className="robot-legs-v2">
          <span />
          <span />
        </div>

        <div className="robot-shadow-v2" />
      </div>

      {mode === "thinking" && (
        <div className="robot-hologram">
          <div>DATA</div>
          <div className="holo-bars">
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      )}

      {mode === "loss" && (
        <div className="robot-analysis">
          <Brain size={13} />
          <span>ANALYZING LOSS...</span>
        </div>
      )}

      <div className={`robot-status-pill ${info.color}`}>
        <span className="robot-status-dot" />
        {info.label}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, positive }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-mute">{label}</span>
        <Icon size={16} className="text-cyan-300" />
      </div>

      <div className="mt-2 text-2xl font-bold text-white">{value}</div>

      {sub && (
        <div
          className={`mt-1 text-xs ${
            positive === true
              ? "text-emerald-300"
              : positive === false
                ? "text-red-300"
                : "text-mute"
          }`}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function PatternCard({ item, risky = false }) {
  const label = item?.pattern || item?.key || item?.name || "Pattern";
  const wr = Number(item?.winrate ?? item?.win_rate ?? 0);
  const samples = Number(item?.samples ?? item?.count ?? 0);
  const confidence = Number(item?.confidence ?? 0);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="font-semibold text-white">{String(label)}</div>
        <span
          className={`rounded-full px-2 py-1 text-[10px] font-bold ${
            risky
              ? "bg-red-500/10 text-red-300"
              : "bg-emerald-500/10 text-emerald-300"
          }`}
        >
          {risky ? "RISKY" : "BEST"}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-mute">WR</div>
          <div className="mt-1 font-bold text-white">{wr.toFixed(1)}%</div>
        </div>
        <div>
          <div className="text-mute">Samples</div>
          <div className="mt-1 font-bold text-white">{samples}</div>
        </div>
        <div>
          <div className="text-mute">Confidence</div>
          <div className="mt-1 font-bold text-white">{confidence.toFixed(0)}%</div>
        </div>
      </div>
    </div>
  );
}

function CombinationCard({ item, risky = false }) {
  const label =
    item?.combination ||
    item?.key ||
    item?.name ||
    item?.factors ||
    "Combination";

  const wr = Number(item?.winrate ?? item?.win_rate ?? 0);
  const samples = Number(item?.samples ?? item?.count ?? 0);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-semibold text-white">
          {Array.isArray(label) ? label.join(" + ") : String(label)}
        </div>

        <span
          className={`shrink-0 text-xs font-bold ${
            risky ? "text-red-300" : "text-emerald-300"
          }`}
        >
          {wr.toFixed(1)}%
        </span>
      </div>

      <div className="mt-2 text-xs text-mute">
        {samples} historical samples
      </div>
    </div>
  );
}


function ActiveSignalsLearning({ signals }) {
  const getStatus = (item) => {
    if (item?.status === "SNAPSHOT_UNAVAILABLE") {
      return {
        label: "WAIT FOR DATA",
        cls: "border-orange-400/20 bg-orange-400/5 text-orange-300",
      };
    }

    if (item?.status === "INSUFFICIENT_DATA") {
      return {
        label: "INSUFFICIENT DATA",
        cls: "border-yellow-400/20 bg-yellow-400/5 text-yellow-300 whitespace-normal break-words text-center leading-tight max-w-[110px]",
      };
    }

    if (item?.recommendation === "FAVORABLE") {
      return {
        label: "FAVORABLE",
        cls: "border-emerald-400/20 bg-emerald-400/5 text-emerald-300",
      };
    }

    if (item?.recommendation === "CAUTION") {
      return {
        label: "CAUTION",
        cls: "border-yellow-400/20 bg-yellow-400/5 text-yellow-300",
      };
    }

    return {
      label: String(item?.recommendation || "WAIT FOR MORE DATA")
        .replaceAll("_", " "),
      cls: "border-cyan-400/20 bg-cyan-400/5 text-cyan-300",
    };
  };

  return (
    <section className="rounded-2xl border border-cyan-400/10 bg-cyan-400/[0.025] p-5">
      <div className="mb-5">
        <div className="flex items-center gap-2">
          <Activity size={17} className="text-cyan-300" />
          <h3 className="font-bold text-white">
            Active Signals Learning
          </h3>
        </div>

        <p className="mt-1 text-xs text-mute">
          Semua signal RUNNING dianalisis satu per satu oleh Learning Engine.
        </p>
      </div>

      {signals.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-mute">
          Tidak ada signal RUNNING saat ini.
        </div>
      ) : (
        <div className="space-y-3">
          {signals.map((item, index) => {
            const status = getStatus(item);
            const hasSnapshot =
              item?.status !== "SNAPSHOT_UNAVAILABLE";

            return (
              <div
                key={`${item?.symbol || "signal"}-${item?.direction || ""}-${index}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-bold text-white">
                        {item?.symbol || "—"}
                      </span>

                      <span className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-bold text-white">
                        {item?.direction || "WAIT"}
                      </span>

                      <span className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-mute">
                        Score {item?.score ?? "—"}
                      </span>

                      <span
                        className={`rounded-lg border px-2 py-1 text-[11px] font-bold ${status.cls}`}
                      >
                        {status.label}
                      </span>
                    </div>

                    <p className="mt-2 text-xs leading-5 text-mute">
                      {hasSnapshot
                        ? "Learning Engine menemukan data historis yang dapat dibandingkan dengan signal ini."
                        : "Signal ini belum memiliki learning snapshot. Data fitur lengkap akan tersedia untuk signal berikutnya."}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 lg:min-w-[330px]">
                    <div className="rounded-xl bg-black/10 p-3">
                      <div className="text-[10px] text-mute">
                        Confidence
                      </div>
                      <div className="mt-1 text-sm font-bold text-cyan-300">
                        {hasSnapshot
                          ? `${Number(item?.confidence || 0).toFixed(1)}%`
                          : "—"}
                      </div>
                    </div>

                    <div className="rounded-xl bg-black/10 p-3">
                      <div className="text-[10px] text-mute">
                        Hist. WR
                      </div>
                      <div className="mt-1 text-sm font-bold text-white">
                        {hasSnapshot
                          ? `${Number(item?.winrate || 0).toFixed(1)}%`
                          : "—"}
                      </div>
                    </div>

                    <div className="rounded-xl bg-black/10 p-3">
                      <div className="text-[10px] text-mute">
                        Samples
                      </div>
                      <div className="mt-1 text-sm font-bold text-white">
                        {hasSnapshot ? item?.samples ?? 0 : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {hasSnapshot && (
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-mute">
                    <span className="rounded-lg bg-white/5 px-2 py-1">
                      WIN {item?.wins ?? 0}
                    </span>
                    <span className="rounded-lg bg-white/5 px-2 py-1">
                      LOSS {item?.losses ?? 0}
                    </span>
                    <span className="rounded-lg bg-white/5 px-2 py-1">
                      Risk {item?.risk || "UNKNOWN"}
                    </span>
                  </div>
                )}

                {Array.isArray(item?.explanation) &&
                  item.explanation.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {item.explanation.slice(0, 3).map((text, i) => (
                        <div
                          key={i}
                          className="text-xs leading-5 text-mute"
                        >
                          • {text}
                        </div>
                      ))}
                    </div>
                  )}

                {typeof item?.explanation === "string" &&
                  item.explanation && (
                    <div className="mt-3 text-xs leading-5 text-mute">
                      {item.explanation}
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}


function LearningPipeline({ active, wins, losses, samples, mode }) {
  const steps = [
    ["MARKET", Activity],
    ["SIGNAL", Target],
    ["ANALYZE", Brain],
    ["WIN", TrendingUp],
    ["LOSS", TrendingDown],
    ["RISK", ShieldAlert],
    ["LEARN", Sparkles],
    ["IMPROVE", Zap],
  ];

  const completed =
    mode === "win" || mode === "loss" ? 8 :
    active ? 3 : 1;

  const progress = Math.min(
    100,
    Math.round((completed / steps.length) * 100)
  );

  return (
    <section className="learning-pipeline" data-testid="learning-pipeline">
      <div className="learning-pipeline-head">
        <div>
          <div className="learning-section-kicker">
            <span className="learning-live-dot" />
            LEARNING ENGINE
          </div>
          <h3>Signal Learning Pipeline</h3>
        </div>

        <div className="learning-pipeline-progress">
          {progress}%
        </div>
      </div>

      <div className="learning-pipeline-line">
        <div
          className="learning-pipeline-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="learning-pipeline-steps">
        {steps.map(([label, Icon], index) => {
          const done = index < completed;
          const thinking = active && index === completed;

          return (
            <div
              key={label}
              className={[
                "learning-pipeline-step",
                done ? "is-done" : "",
                thinking ? "is-thinking" : "",
              ].join(" ")}
            >
              <div className="learning-pipeline-node">
                <Icon size={14} />
              </div>

              <div className="learning-pipeline-label">
                {label}
              </div>
            </div>
          );
        })}
      </div>

      <div className="learning-pipeline-footer">
        <div>
          <span className="learning-win-dot" />
          WIN <b>{wins || 0}</b>
        </div>

        <div>
          <span className="learning-loss-dot" />
          LOSS <b>{losses || 0}</b>
        </div>

        <div>
          <b>{samples || 0}</b> historical samples
        </div>
      </div>
    </section>
  );
}


function LearningOutcomeCard({ wins, losses, winrate, samples }) {
  const safeWins = Number(wins || 0);
  const safeLosses = Number(losses || 0);
  const total = safeWins + safeLosses;

  const winPct = total > 0
    ? Math.round((safeWins / total) * 100)
    : Number(winrate || 0);

  const lossPct = total > 0
    ? Math.round((safeLosses / total) * 100)
    : Math.max(0, 100 - winPct);

  return (
    <section className="learning-outcome" data-testid="learning-outcome">
      <div className="learning-outcome-head">
        <div>
          <div className="learning-section-kicker">
            <span className="learning-live-dot" />
            HISTORICAL OUTCOME
          </div>
          <h3>WIN vs LOSS</h3>
        </div>

        <div className="learning-sample-pill">
          {samples || total} samples
        </div>
      </div>

      <div className="learning-outcome-grid">
        <div className="learning-outcome-side learning-win">
          <div className="learning-outcome-icon">✓</div>
          <div>
            <span>WIN</span>
            <strong>{safeWins}</strong>
            <small>successful outcomes</small>
          </div>
        </div>

        <div className="learning-outcome-vs">VS</div>

        <div className="learning-outcome-side learning-loss">
          <div className="learning-outcome-icon">×</div>
          <div>
            <span>LOSS</span>
            <strong>{safeLosses}</strong>
            <small>failed outcomes</small>
          </div>
        </div>
      </div>

      <div className="learning-outcome-bar">
        <div
          className="learning-outcome-win-bar"
          style={{ width: `${winPct}%` }}
        />
        <div
          className="learning-outcome-loss-bar"
          style={{ width: `${lossPct}%` }}
        />
      </div>
    </section>
  );
}


function LearningConfidence({ confidence, recommendation, risk }) {
  const value = Math.max(
    0,
    Math.min(100, Number(confidence || 0))
  );

  const rec = String(recommendation || "WAIT_FOR_DATA")
    .replace(/_/g, " ");

  return (
    <section
      className="learning-confidence"
      data-testid="learning-confidence"
    >
      <div className="learning-confidence-head">
        <div>
          <div className="learning-section-kicker">
            <span className="learning-live-dot" />
            MODEL CONFIDENCE
          </div>
          <h3>Learning Confidence</h3>
        </div>

        <div className="learning-confidence-value">
          {value.toFixed(0)}%
        </div>
      </div>

      <div className="learning-confidence-track">
        <div
          className="learning-confidence-fill"
          style={{ width: `${value}%` }}
        />
        <span style={{ left: `${value}%` }} />
      </div>

      <div className="learning-confidence-meta">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>

      <div className="learning-confidence-status">
        <div>
          <small>RECOMMENDATION</small>
          <b>{rec}</b>
        </div>

        <div>
          <small>RISK</small>
          <b>{String(risk || "UNKNOWN").replace(/_/g, " ")}</b>
        </div>

        <div>
          <small>ENGINE</small>
          <b>V4 ACTIVE</b>
        </div>
      </div>
    </section>
  );
}

export function LearningPage() {
  const [learning, setLearning] = useState(null);

  const activeSignals = Array.isArray(learning?.active_signals)
    ? learning.active_signals
    : [];

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [step, setStep] = useState(0);
  const [simulation, setSimulation] = useState(null);
  const [signalPulse, setSignalPulse] = useState(false);
  const [autoAnalyzing, setAutoAnalyzing] = useState(false);
  const [previousSignalKey, setPreviousSignalKey] = useState("");
  const [recommendationHistory, setRecommendationHistory] = useState([]);
  const [recommendationPerformance, setRecommendationPerformance] = useState(null);

  const getSignalKey = (data) => {
    const signal =
      data?.current_signal ||
      data?.current_signal_analysis ||
      data?.current_signals ||
      null;

    if (!signal) return "";

    if (Array.isArray(signal)) {
      return signal
        .map((item) =>
          [
            item?.symbol,
            item?.direction,
            item?.entry,
            item?.signal_id,
          ].join(":")
        )
        .sort()
        .join("|");
    }

    return [
      signal?.symbol,
      signal?.direction,
      signal?.entry,
      signal?.signal_id,
    ].join(":");
  };

  const load = useCallback(async (isAuto = false) => {
    try {
      // Auto analysis hanya aktif jika benar-benar ada signal baru.

      const response = await apiGet("/learning/overview");
      const data = response?.data ?? response;

      const [historyResponse, performanceResponse] = await Promise.all([
        apiGet("/learning/recommendation-history"),
        apiGet("/learning/recommendation-performance"),
      ]);

      const historyData = historyResponse?.data ?? historyResponse;
      const performanceData = performanceResponse?.data ?? performanceResponse;

      setRecommendationHistory(Array.isArray(historyData?.items) ? historyData.items : []);
      setRecommendationPerformance(performanceData || null);

      const nextKey = getSignalKey(data);

      const detectedNewSignal =
        isAuto &&
        previousSignalKey &&
        nextKey &&
        nextKey !== previousSignalKey;

      if (detectedNewSignal) {
        setAutoAnalyzing(true);
        setSignalPulse(true);
        setStep(0);

        window.setTimeout(() => {
          setSignalPulse(false);
        }, 3500);

        window.setTimeout(() => {
          setAutoAnalyzing(false);
        }, 1200);
      }

      if (nextKey) {
        setPreviousSignalKey(nextKey);
      }

      setLearning(data);
    } catch (error) {
      console.error("Learning overview error:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);


    }
  }, [previousSignalKey]);

  useEffect(() => {
    load(false);

    const timer = setInterval(() => {
      load(true);
    }, 6000);

    const thinkingTimer = setInterval(() => {
      setStep((value) => (value + 1) % THINKING_STEPS.length);
    }, 1800);

    return () => {
      clearInterval(timer);
      clearInterval(thinkingTimer);
    };
  }, [load]);

  useEffect(() => {
    const readSimulation = () => {
      try {
        const raw = localStorage.getItem(SIM_KEY);
        if (!raw) {
          setSimulation(null);
          return;
        }

        const parsed = JSON.parse(raw);
        setSimulation(parsed?.state || null);
      } catch {
        setSimulation(null);
      }
    };

    readSimulation();

    const onSimulation = (event) => {
      setSimulation(event?.detail?.state || null);
    };

    window.addEventListener("nakedchart-learning-simulation", onSimulation);
    window.addEventListener("storage", readSimulation);

    return () => {
      window.removeEventListener(
        "nakedchart-learning-simulation",
        onSimulation
      );
      window.removeEventListener("storage", readSimulation);
    };
  }, []);

  const trades = learning?.trades_analyzed ?? learning?.total_trades ?? 0;
  const wins = learning?.wins ?? 0;
  const losses = learning?.losses ?? 0;
  const winrate = Number(learning?.winrate ?? learning?.win_rate ?? 0);

  const bestPatterns = learning?.best_patterns ?? [];
  const riskyPatterns = learning?.risky_patterns ?? [];
  const bestCombinations = learning?.best_combinations ?? [];
  const riskyCombinations = learning?.risky_combinations ?? [];

  const currentSignal = normalizeCurrentSignal(learning);

  const baseMode = useMemo(
    () => getRobotMode(learning, simulation),
    [learning, simulation]
  );

  // Signal baru terdeteksi: robot masuk mode analyzing.
  // Setelah selesai, kembali ke hasil learning.
  const mode = autoAnalyzing ? "thinking" : baseMode;

  const modeInfo = MODE_INFO[mode] || MODE_INFO.idle;

  const confidence = Number(
    currentSignal?.confidence ??
      currentSignal?.confidence_score ??
      0
  );

  const currentWinrate = Number(
    currentSignal?.winrate ??
      currentSignal?.win_rate ??
      0
  );

  const currentSamples = Number(
    currentSignal?.samples ??
      currentSignal?.sample_count ??
      0
  );

  const recommendation =
    currentSignal?.recommendation ||
    learning?.recommendation ||
    "WAIT_FOR_MORE_DATA";

  const simulationActive = Boolean(simulation);

  const clearSimulation = () => {
    localStorage.removeItem(SIM_KEY);
    setSimulation(null);
  };

  return (
    <div className="space-y-5" data-testid="learning-page">
      <div className="relative overflow-hidden rounded-3xl border border-cyan-400/10 bg-gradient-to-br from-cyan-400/[0.07] via-white/[0.02] to-purple-500/[0.06]">
        <div className="grid gap-5 p-5 md:grid-cols-[280px_1fr] md:p-7">
          <div>
            <RobotLearningV2 mode={mode} />

            {autoAnalyzing && (
              <div className="mt-3 text-center text-xs text-cyan-300 animate-pulse">
                🤖 Menganalisis signal baru...
              </div>
            )}

            {signalPulse && !autoAnalyzing && (
              <div className="mt-2 text-center text-xs text-emerald-300 animate-pulse">
                ✓ Analisis signal selesai
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[10px] font-bold tracking-wider text-cyan-300">
                NAKED CHART LEARNING ENGINE
              </span>

              {simulationActive && (
                <button
                  type="button"
                  onClick={clearSimulation}
                  className="rounded-full border border-yellow-400/20 bg-yellow-400/10 px-3 py-1 text-[10px] font-bold text-yellow-300 transition hover:bg-yellow-400/20"
                >
                  SIMULATION • RESET
                </button>
              )}
            </div>

            <h2 className="mt-3 text-2xl font-bold text-white md:text-3xl">
              {simulationActive
                ? `Simulation: ${modeInfo.label}`
                : "Robot sedang belajar dari market"}
            </h2>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-mute">
              {simulationActive
                ? modeInfo.description
                : loading || refreshing
                  ? THINKING_STEPS[step]
                  : modeInfo.description}
            </p>

            {!simulationActive && (
              <div className="mt-5 flex items-center gap-2 text-xs text-mute">
                <Activity size={14} className="text-cyan-300" />
                <span>
                  WIN {wins} • LOSS {losses} • {trades} samples
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <LearningPipeline
        active={autoAnalyzing || loading}
        wins={currentSignal?.wins ?? 0}
        losses={currentSignal?.losses ?? 0}
        samples={currentSignal?.samples ?? 0}
        mode={mode}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard
          icon={Activity}
          label="Trades Analyzed"
          value={trades}
          sub="Historical records"
        />

        <MetricCard
          icon={TrendingUp}
          label="Wins"
          value={wins}
          sub={`${winrate.toFixed(1)}% winrate`}
          positive
        />

        <MetricCard
          icon={TrendingDown}
          label="Losses"
          value={losses}
          sub="Used for learning"
          positive={losses === 0 ? true : undefined}
        />

        <MetricCard
          icon={Brain}
          label="Learning Status"
          value={
            (learning?.deep_learning?.status || learning?.status || "ACTIVE") === "INSUFFICIENT_DATA" ? (
              <>INSUFFICIENT<br />DATA</>
            ) : (
              learning?.deep_learning?.status || learning?.status || "ACTIVE"
            )
          }
          sub={`${learning?.deep_learning?.samples ?? 0} deep samples`}
        />
      </div>

      <div
        data-testid="learning-outcome-confidence"
        className="grid gap-5 lg:grid-cols-2"
      >
        <LearningOutcomeCard
          wins={learning?.wins ?? 0}
          losses={learning?.losses ?? 0}
          winrate={learning?.winrate ?? 0}
          samples={learning?.trades_analyzed ?? 0}
        />

        <LearningConfidence
          confidence={currentSignal?.confidence ?? 0}
          recommendation={currentSignal?.recommendation}
          risk={currentSignal?.risk}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Target size={17} className="text-emerald-300" />
                <h3 className="font-bold text-white">Best Patterns</h3>
              </div>
              <p className="mt-1 text-xs text-mute">
                Faktor yang paling sering menghasilkan WIN.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {bestPatterns.length ? (
              bestPatterns.slice(0, 6).map((item, index) => (
                <PatternCard key={index} item={item} />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-white/10 p-5 text-center text-xs text-mute">
                Belum cukup data untuk menentukan best pattern.
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <ShieldAlert size={17} className="text-red-300" />
              <h3 className="font-bold text-white">Risky Patterns</h3>
            </div>
            <p className="mt-1 text-xs text-mute">
              Faktor yang perlu lebih diwaspadai.
            </p>
          </div>

          <div className="space-y-3">
            {riskyPatterns.length ? (
              riskyPatterns.slice(0, 6).map((item, index) => (
                <PatternCard key={index} item={item} risky />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-white/10 p-5 text-center text-xs text-mute">
                Belum cukup data untuk menentukan risky pattern.
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles size={17} className="text-cyan-300" />
            <h3 className="font-bold text-white">Winning Combinations</h3>
          </div>

          <div className="space-y-3">
            {bestCombinations.length ? (
              bestCombinations.slice(0, 6).map((item, index) => (
                <CombinationCard key={index} item={item} />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-white/10 p-5 text-center text-xs text-mute">
                Combination belum memiliki sample yang cukup.
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <div className="mb-4 flex items-center gap-2">
            <ShieldAlert size={17} className="text-orange-300" />
            <h3 className="font-bold text-white">Losing Combinations</h3>
          </div>

          <div className="space-y-3">
            {riskyCombinations.length ? (
              riskyCombinations.slice(0, 6).map((item, index) => (
                <CombinationCard key={index} item={item} risky />
              ))
            ) : (
              <div className="rounded-xl border border-dashed border-white/10 p-5 text-center text-xs text-mute">
                Belum cukup data untuk menemukan kombinasi buruk.
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-cyan-400/10 bg-cyan-400/[0.025] p-5">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Zap size={17} className="text-cyan-300" />
              <h3 className="font-bold text-white">Current Signal Analyzer</h3>
            </div>

            <p className="mt-1 text-xs text-mute">
              Membandingkan signal sekarang dengan pola WIN dan LOSS historis.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              setRefreshing(true);
              load();
            }}
            disabled={refreshing}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white transition hover:bg-white/10 disabled:opacity-50"
          >
            <RefreshCw
              size={14}
              className={refreshing ? "animate-spin" : ""}
            />
            Analyze Again
          </button>
        </div>

        {currentSignal?.selection_reason?.length > 0 && (
          <div className="mt-5 rounded-xl border border-cyan-400/10 bg-cyan-400/[0.025] p-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
              Why This Signal Was Selected
            </div>

            <div className="space-y-1.5">
              {currentSignal.selection_reason.map((reason, index) => (
                <div
                  key={index}
                  className="text-xs leading-relaxed text-mute"
                >
                  ✓ {reason}
                </div>
              ))}
            </div>
          </div>
        )}

        {currentSignal ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-xl bg-white/[0.03] p-4">
              <div className="text-xs text-mute">Symbol</div>
              <div className="mt-1 font-bold text-white">
                {currentSignal.symbol || "—"}
              </div>
            </div>

            <div className="rounded-xl bg-white/[0.03] p-4">
              <div className="text-xs text-mute">Direction</div>
              <div className="mt-1 font-bold text-white">
                {currentSignal.direction || "WAIT"}
              </div>
            </div>

            <div className="rounded-xl bg-white/[0.03] p-4">
              <div className="text-xs text-mute">Confidence</div>
              <div className="mt-1 font-bold text-cyan-300">
                {confidence.toFixed(1)}%
              </div>
            </div>

            <div className="rounded-xl bg-white/[0.03] p-4">
              <div className="text-xs text-mute">Historical WR</div>
              <div className="mt-1 font-bold text-white">
                {currentWinrate.toFixed(1)}%
              </div>
            </div>

            <div className="rounded-xl bg-white/[0.03] p-4">
              <div className="text-xs text-mute">Samples</div>
              <div className="mt-1 font-bold text-white">
                {currentSamples}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-mute">
            Belum ada current signal yang dapat dianalisis.
          </div>
        )}

        <div className="mt-5 rounded-xl border border-white/10 bg-black/10 p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-mute">Recommendation</span>
            <span className="text-sm font-bold text-white">
              {String(recommendation).replaceAll("_", " ")}
            </span>
          </div>

          {currentSignal?.explanation && (
            <p className="mt-3 text-sm leading-6 text-mute">
              {currentSignal.explanation}
            </p>
          )}

          {currentSignal?.matched_patterns?.length > 0 && (
            <div className="mt-4 space-y-2">
              {currentSignal.matched_patterns.slice(0, 5).map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-xs text-mute"
                >
                  <ChevronRight size={13} className="text-cyan-300" />
                  {typeof item === "string"
                    ? item
                    : item?.pattern || item?.key || "Matched pattern"}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <RecommendedSignalCard data={learning?.recommended_signal} />

      <MarketRegimeCard data={learning?.recommended_signal} />

      <SymbolPerformanceCard performance={learning?.symbol_performance} />

      <RecommendationPerformanceCard data={recommendationPerformance} />

      <RecommendationHistoryCard items={recommendationHistory} />

      <ActiveSignalsLearning signals={activeSignals} />

      <div className="rounded-2xl border border-white/10 bg-white/[0.015] p-4 text-xs text-mute">
        <div className="flex items-center gap-2">
          <Brain size={14} className="text-cyan-300" />
          <span>
            Learning Engine V4 hanya membaca hasil historis dan menganalisis
            pola. Strategy, BUY/SELL, entry, SL dan TP tidak diubah.
          </span>
        </div>
      </div>
    </div>
  );
}

function RecommendedSignalCard({ data }) {
  const rec = String(data?.recommendation || "WAIT_FOR_DATA").toUpperCase();
  const score = data?.final_score;
  const regime = data?.market_regime || "N/A";

  const recClass =
    rec === "TRADE"
      ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
      : rec === "CAUTION"
        ? "border-yellow-400/30 bg-yellow-400/10 text-yellow-300"
        : "border-white/10 bg-white/[0.03] text-cyan-300";

  return (
    <section className="rounded-2xl border border-cyan-400/10 bg-cyan-400/[0.025] p-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Target size={18} className="text-cyan-300" />
            <h3 className="font-bold text-white">Recommended Signal</h3>
          </div>
          <p className="mt-1 text-xs text-mute">
            Keputusan gabungan Technical, Market Regime, Performance dan Learning.
          </p>
        </div>

        <span className={`rounded-xl border px-4 py-2 text-xs font-bold ${recClass}`}>
          {rec.replaceAll("_", " ")}
        </span>
      </div>

      {data?.symbol ? (
        <>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="text-2xl font-black text-white">
              {data.symbol}
            </span>
            <span className="rounded-lg bg-white/5 px-3 py-1 text-sm font-bold text-white">
              {data.direction || "WAIT"}
            </span>
            {score != null && (
              <span className="rounded-lg bg-cyan-400/10 px-3 py-1 text-sm font-bold text-cyan-300">
                FINAL SCORE {Number(score).toFixed(0)}
              </span>
            )}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Technical", data.technical_score],
              ["Market Regime", data.market_regime_score],
              ["Performance", data.performance_score],
              ["Learning", data.learning_score],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl bg-white/[0.03] p-4">
                <div className="text-[10px] uppercase tracking-wider text-mute">
                  {label}
                </div>
                <div className="mt-1 text-xl font-bold text-white">
                  {value != null ? `${Number(value).toFixed(0)}` : "N/A"}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-white/10 bg-black/10 p-4">
              <div className="text-[10px] text-mute">MARKET REGIME</div>
              <div className="mt-1 font-bold text-cyan-300">{regime}</div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/10 p-4">
              <div className="text-[10px] text-mute">PERFORMANCE</div>
              <div className="mt-1 font-bold text-white">
                {data.performance || "INSUFFICIENT_DATA"}
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-black/10 p-4">
              <div className="text-[10px] text-mute">DATA STATUS</div>
              <div className="mt-1 font-bold text-white">
                {data?.reason ? "ANALYZED" : "WAITING"}
              </div>
            </div>
          </div>

          {data?.reason && (
            <div className="mt-4 rounded-xl border border-white/10 bg-black/10 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                Alasan
              </div>
              <p className="mt-2 text-sm leading-6 text-mute">
                {data.reason}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="mt-5 rounded-xl border border-dashed border-white/10 p-8 text-center">
          <div className="text-sm font-semibold text-white">
            Belum ada signal untuk direkomendasikan
          </div>
          <div className="mt-1 text-xs text-mute">
            Recommended Signal akan aktif otomatis ketika signal baru tersedia.
          </div>
        </div>
      )}
    </section>
  );
}

function MarketRegimeCard({ data }) {
  const regime = data?.market_regime || "N/A";
  const confidence = data?.market_regime_score;

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center gap-2">
        <Activity size={17} className="text-cyan-300" />
        <h3 className="font-bold text-white">Market Regime Detector</h3>
      </div>

      <p className="mt-1 text-xs text-mute">
        Membaca kondisi market sebagai layer tambahan tanpa mengubah strategy.
      </p>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl bg-white/[0.03] p-4">
          <div className="text-[10px] text-mute">REGIME</div>
          <div className="mt-1 font-bold text-cyan-300">{regime}</div>
        </div>

        <div className="rounded-xl bg-white/[0.03] p-4">
          <div className="text-[10px] text-mute">CONFIDENCE</div>
          <div className="mt-1 font-bold text-white">
            {confidence != null ? `${Number(confidence).toFixed(0)}%` : "N/A"}
          </div>
        </div>

        <div className="rounded-xl bg-white/[0.03] p-4">
          <div className="text-[10px] text-mute">VOLATILITY</div>
          <div className="mt-1 font-bold text-white">
            {data?.volatility || "N/A"}
          </div>
        </div>

        <div className="rounded-xl bg-white/[0.03] p-4">
          <div className="text-[10px] text-mute">MTF ALIGNMENT</div>
          <div className="mt-1 font-bold text-white">
            {data?.mtf_alignment != null ? data.mtf_alignment : "N/A"}
          </div>
        </div>
      </div>
    </section>
  );
}

function SymbolPerformanceCard({ performance }) {
  const items = performance && typeof performance === "object"
    ? Object.entries(performance)
    : [];

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center gap-2">
        <Brain size={17} className="text-purple-300" />
        <h3 className="font-bold text-white">Adaptive Learning / Symbol Performance</h3>
      </div>

      <p className="mt-1 text-xs text-mute">
        Performa historis setiap symbol digunakan sebagai input tambahan.
      </p>

      <div className="mt-5 space-y-2">
        {items.length ? items.map(([symbol, item]) => (
          <div
            key={symbol}
            className="grid grid-cols-2 gap-3 rounded-xl bg-white/[0.03] p-3 md:grid-cols-5"
          >
            <div className="font-bold text-white">{symbol}</div>
            <div>
              <div className="text-[9px] text-mute">TRADES</div>
              <div className="text-xs font-bold text-white">{item?.trades ?? 0}</div>
            </div>
            <div>
              <div className="text-[9px] text-mute">WINRATE</div>
              <div className="text-xs font-bold text-white">
                {Number(item?.winrate ?? 0).toFixed(1)}%
              </div>
            </div>
            <div>
              <div className="text-[9px] text-mute">CONFIDENCE</div>
              <div className="text-xs font-bold text-cyan-300">
                {Number(item?.confidence ?? 0).toFixed(1)}%
              </div>
            </div>
            <div>
              <div className="text-[9px] text-mute">PERFORMANCE</div>
              <div className="text-xs font-bold text-white">
                {item?.performance || "N/A"}
              </div>
            </div>
          </div>
        )) : (
          <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-xs text-mute">
            Belum ada data symbol performance.
          </div>
        )}
      </div>
    </section>
  );
}

function RecommendationPerformanceCard({ data }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center gap-2">
        <TrendingUp size={17} className="text-emerald-300" />
        <h3 className="font-bold text-white">Recommendation Performance</h3>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          ["TOTAL", data?.total ?? 0],
          ["COMPLETED", data?.completed ?? 0],
          ["WIN", data?.wins ?? 0],
          ["LOSS", data?.losses ?? 0],
          ["WINRATE", `${Number(data?.winrate ?? 0).toFixed(1)}%`],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl bg-white/[0.03] p-4">
            <div className="text-[10px] text-mute">{label}</div>
            <div className="mt-1 text-xl font-bold text-white">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-xl bg-white/[0.03] p-4">
        <div className="text-[10px] text-mute">TOTAL PIPS</div>
        <div className="mt-1 text-lg font-bold text-cyan-300">
          {Number(data?.total_pips ?? 0).toFixed(2)}
        </div>
      </div>
    </section>
  );
}

function RecommendationHistoryCard({ items }) {
  const [page, setPage] = useState(0);
  const perPage = 5;
  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / perPage));

  const visibleItems = (items || []).slice(
    page * perPage,
    (page + 1) * perPage
  );

  useEffect(() => {
    if (page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ChevronRight size={17} className="text-cyan-300" />
          <h3 className="font-bold text-white">Recommendation History</h3>
        </div>

        {items?.length ? (
          <span className="text-[10px] font-semibold text-mute">
            Page {page + 1} / {totalPages}
          </span>
        ) : null}
      </div>

      <p className="mt-1 text-xs text-mute">
        Hanya recommendation dengan keputusan TRADE yang dicatat.
      </p>

      <div className="mt-5 space-y-2">
        {visibleItems.length ? visibleItems.map((item, index) => (
          <div
            key={item?._id || index}
            className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3"
          >
            <div className="min-w-[72px] font-bold text-white">
              {item?.symbol || "—"}
            </div>

            <div className="rounded-lg bg-white/5 px-2 py-1 text-xs font-bold text-white">
              {item?.direction || "—"}
            </div>

            <div className="text-xs font-semibold text-cyan-300">
              Score {item?.final_score ?? "—"}
            </div>

            <div className="text-xs text-mute">
              {item?.market_regime || "N/A"}
            </div>

            <div className="text-xs font-bold text-white">
              {item?.status || "PENDING"}
            </div>

            <div className="text-xs text-mute">
              {item?.result || "—"}
            </div>

            <div className="ml-auto text-xs font-bold text-cyan-300">
              {item?.pips != null ? String(item.pips) + " pips" : "—"}
            </div>
          </div>
        )) : (
          <div className="rounded-xl border border-dashed border-white/10 p-7 text-center text-xs text-mute">
            Belum ada recommendation TRADE.
            <br />
            History akan muncul otomatis setelah signal mendapat keputusan TRADE.
          </div>
        )}
      </div>

      {items?.length > perPage && (
        <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[10px] font-bold text-white disabled:opacity-30"
          >
            ← PREV
          </button>

          <span className="text-[10px] font-semibold text-mute">
            {page * perPage + 1}–{Math.min((page + 1) * perPage, items.length)} dari {items.length}
          </span>

          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[10px] font-bold text-white disabled:opacity-30"
          >
            NEXT →
          </button>
        </div>
      )}
    </section>
  );
}

